import { parseLocale } from "@repo/fika-shared/src/locale"
import { translate } from "@repo/fika-shared/src/translate"
import { createMemo, Show } from "solid-js"
import { Icon } from "@/designSystem/components/icon"
import { createFragment, type FragmentOf, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { badgeClass, upgradeBadgeClass } from "./content-access-badge.css"

export const ContentAccessBadgeFragment = graphql(`
  fragment ContentAccessBadge on PublishedPost {
    contentAccess {
      hasAccess
      restricted
      requiredTiers {
        id
        name
      }
    }
    publication {
      sub {
        tier {
          id
          name
        }
      }
    }
  }
`)

export function ContentAccessBadge(props: {
  post: FragmentOf<typeof ContentAccessBadgeFragment>
  onUpgrade?: (e: MouseEvent) => void
  class?: string
}) {
  const { LL, locale } = useI18nContext()
  const post = createFragment(ContentAccessBadgeFragment, () => props.post)

  const hasAccess = createMemo(() => post().contentAccess.hasAccess)
  const isRestricted = createMemo(() => post().contentAccess.restricted)
  const requiredTiers = createMemo(
    () => post().contentAccess.requiredTiers ?? [],
  )
  const hasTierRequirement = createMemo(() => requiredTiers().length > 0)
  const userTier = createMemo(() => post().publication.sub?.tier)
  const lockIcon = createMemo(() =>
    hasAccess() ? ("lock-open" as const) : ("lock" as const),
  )

  const interfaceLocale = createMemo(() => parseLocale(locale()))
  const tierLabel = createMemo(() => {
    const locale = interfaceLocale()
    const ut = userTier()
    if (ut) return translate(ut.name, locale) ?? ut.name
    return requiredTiers()
      .map(t => translate(t.name, locale) ?? t.name)
      .join(" / ")
  })

  return (
    <Show when={isRestricted()}>
      <Show when={!hasTierRequirement()}>
        <span class={`${badgeClass} ${props.class ?? ""}`}>
          <Icon name={lockIcon()} size="12" color="sand500" />
          {LL().subscriptions.subscribersOnly()}
        </span>
      </Show>
      <Show when={hasTierRequirement() && !hasAccess() && props.onUpgrade}>
        <button
          type="button"
          class={`${upgradeBadgeClass} ${props.class ?? ""}`}
          onClick={props.onUpgrade}
        >
          <Icon name="lock" size="12" />
          {LL().privatePost.upgradeCta()}
        </button>
      </Show>
      <Show when={hasTierRequirement() && !hasAccess() && !props.onUpgrade}>
        <span class={`${badgeClass} ${props.class ?? ""}`}>
          <Icon name="lock" size="12" color="sand500" />
          {tierLabel()}
        </span>
      </Show>
      <Show when={hasTierRequirement() && hasAccess()}>
        <span class={`${badgeClass} ${props.class ?? ""}`}>
          <Icon name="lock-open" size="12" color="sand500" />
          {tierLabel()}
        </span>
      </Show>
    </Show>
  )
}
